import { Component, Input } from '@angular/core';
import { PaymentModel } from '@app/events/shared/payment.model';
import { formatCurrency, CurrencyPipe } from '@angular/common';
import { LucideAngularModule, Info } from 'lucide-angular';

@Component({
  selector: 'app-payment-amount-wrapper',
  template: `
    <div class="group relative inline-flex items-center gap-1.5">
      <span>{{ formattedAmount }}</span>

      @if (hasCalculationFacts) {
        <button
          #tooltipTrigger
          type="button"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help focus:outline-none"
          (mouseenter)="onMouseEnter($event)"
          (mouseleave)="showTooltip = false"
        >
          <lucide-icon [img]="InfoIcon" [size]="14"></lucide-icon>
        </button>

        @if (showTooltip) {
          <div
            class="fixed z-[9999] w-[28rem] p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl text-sm"
            [style.left.px]="tooltipLeft"
            [style.top.px]="tooltipTop"
            (mouseenter)="showTooltip = true"
            (mouseleave)="showTooltip = false"
          >
            <div class="space-y-3">
              <!-- Payment Lock Status Indicator (not shown for reservation fee - always fixed £2000) -->
              @if (calculationFacts.is_locked !== undefined && !item.is_reservation_fee) {
                <div class="pb-3 border-b border-gray-200 dark:border-gray-700">
                  @if (calculationFacts.is_locked) {
                    <div class="flex items-center gap-2 text-sm">
                      <span class="inline-flex items-center px-2.5 py-1 rounded-md bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-medium">
                        <svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                        </svg>
                        Fully Paid
                      </span>
                      <span class="text-xs text-gray-600 dark:text-gray-400 italic">Amount locked (won't change if products added)</span>
                    </div>
                  } @else if (item.amount_paid && item.amount_paid > 0) {
                    <div class="flex items-center gap-2 text-sm">
                      <span class="inline-flex items-center px-2.5 py-1 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 font-medium">
                        <svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clip-rule="evenodd"/>
                        </svg>
                        Partially Paid
                      </span>
                      <span class="text-xs text-gray-600 dark:text-gray-400 italic">Amount may update if products added</span>
                    </div>
                  } @else {
                    <div class="flex items-center gap-2 text-sm">
                      <span class="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                        <svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clip-rule="evenodd"/>
                        </svg>
                        Unpaid
                      </span>
                      <span class="text-xs text-gray-600 dark:text-gray-400 italic">Amount may update if products added</span>
                    </div>
                  }
                </div>
              }

              <!-- Base Amount - Always show prominently at top -->
              @if (calculationFacts.base_amount !== undefined) {
                <div class="space-y-2">
                  <div class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Base Amount</div>
                  <div class="text-2xl font-bold text-gray-900 dark:text-white">
                    {{ calculationFacts.base_amount | currency: 'GBP' }}
                  </div>
                  @if (calculationFacts.stage_facts && calculationFacts.stage_facts.length > 0) {
                    <div class="text-xs space-y-0.5 text-gray-600 dark:text-gray-400">
                      @for (fact of calculationFacts.stage_facts; track $index) {
                        <div>{{ fact }}</div>
                      }
                    </div>
                  }
                </div>
              }

              <!-- Adjustments Section (Combined Credits) -->
              @if (calculationFacts.credits_received && calculationFacts.credits_received.length > 0) {
                <div class="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                  <div class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Adjustments</div>
                  <div class="space-y-2">
                    @for (credit of calculationFacts.credits_received; track $index) {
                      <div class="space-y-1">
                        <div class="flex justify-between items-start">
                          <span class="text-gray-700 dark:text-gray-300">{{ credit.reason }}</span>
                          <span class="font-semibold text-emerald-600 dark:text-emerald-400">
                            -{{ credit.amount | currency: 'GBP' }}
                          </span>
                        </div>
                        @if (credit.amount_absorbed !== undefined && credit.amount_passed !== undefined) {
                          <div class="pl-3 text-xs space-y-0.5 text-gray-600 dark:text-gray-400">
                            @if (credit.amount_absorbed > 0) {
                              <div class="flex justify-between">
                                <span>Applied to this payment:</span>
                                <span class="font-medium">-{{ credit.amount_absorbed | currency: 'GBP' }}</span>
                              </div>
                            }
                            @if (credit.amount_passed > 0) {
                              <div class="flex justify-between">
                                <span>Passed to next payment:</span>
                                <span class="font-medium">{{ credit.amount_passed | currency: 'GBP' }}</span>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- Final Amount Due -->
              @if (calculationFacts.base_amount !== undefined) {
                <div class="pt-3 border-t-2 border-gray-300 dark:border-gray-600 space-y-1">
                  <div class="flex justify-between items-center">
                    <span class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Amount Due</span>
                    <span class="text-xl font-bold text-gray-900 dark:text-white">
                      {{ item.amount | currency: 'GBP' }}
                    </span>
                  </div>
                  @if (item.amount_paid && item.amount_paid > 0) {
                    <div class="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                      <span>Amount Paid:</span>
                      <span class="font-medium text-emerald-600 dark:text-emerald-400">{{ item.amount_paid | currency: 'GBP' }}</span>
                    </div>
                    @if (item.amount - item.amount_paid !== 0) {
                      <div class="flex justify-between text-xs">
                        <span class="text-gray-600 dark:text-gray-400">Remaining:</span>
                        <span class="font-semibold" [class]="(item.amount - item.amount_paid) > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
                          {{ item.amount - item.amount_paid | currency: 'GBP' }}
                        </span>
                      </div>
                    }
                  }
                </div>
              }

              <!-- Credits Given (if overpayment created) -->
              @if (calculationFacts.credits_given && calculationFacts.credits_given.length > 0) {
                <div class="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                  <div class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Credits Created</div>
                  <div class="space-y-1">
                    @for (creditGiven of calculationFacts.credits_given; track $index) {
                      <div class="flex justify-between text-sm">
                        <span class="text-gray-700 dark:text-gray-300">{{ creditGiven.reason || 'Credit to next payment' }}</span>
                        <span class="font-semibold text-purple-600 dark:text-purple-400">
                          {{ creditGiven.amount | currency: 'GBP' }}
                        </span>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- Standard Help Text -->
              @if (standardHelpText) {
                <div class="pt-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400 italic">
                  {{ standardHelpText }}
                </div>
              }
            </div>
          </div>
        }
      }
    </div>
  `,
  imports: [CurrencyPipe, LucideAngularModule],
  standalone: true,
})
export class PaymentAmountWrapperComponent {
  @Input('item') item!: PaymentModel;
  @Input('event') event?: any;

  protected readonly InfoIcon = Info;
  showTooltip = false;
  tooltipLeft = 0;
  tooltipTop = 0;

  constructor() {}

  onMouseEnter(event: MouseEvent) {
    // Calculate tooltip position relative to the mouse/icon position
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Position tooltip to the right and slightly above the icon
    this.tooltipLeft = rect.right + 10;
    this.tooltipTop = Math.max(10, rect.top - 50); // Prevent going off top of screen

    this.showTooltip = true;
  }

  get formattedAmount(): string {
    return formatCurrency(this.item.amount, 'en-GB', '£');
  }

  get hasCalculationFacts(): boolean {
    // Always show tooltip if we have any facts, credits, or it's a payment
    return !!this.calculationFacts.stage_facts?.length ||
           !!this.calculationFacts.credits_received?.length ||
           !!this.item.credit_info?.credit_received ||
           !!this.item.credit_info?.credit_given ||
           !!this.standardHelpText ||
           !!this.item.amount; // Show for any payment with an amount
  }

  get calculationFacts(): any {
    return (this.item as any).calculation_facts || {
      stage_facts: [],
      base_amount: this.item.amount,
      final_amount: this.item.amount,
      credits_received: [],
      credits_given: [],
      overpayment: 0
    };
  }

  get standardHelpText(): string | null {
    // Return standard help text based on payment type
    if (this.item.description === 'First Payment') {
      return 'Reservation fee automatically offsets venue & accommodation once received';
    } else if (this.item.description === 'Second Payment') {
      return 'Fixed at 50% of the contracted minimum spend. Extra spend moves to Final Payment';
    } else if (this.item.description === 'Final payment + Deposit') {
      return 'Remaining balance + £500 deposit. Any new products after confirmation land here';
    }
    return null;
  }
}
